import type { ReactElement } from 'react';
import { useEffect } from 'react';

import type { FilterButtonProps } from './FilterButton';
import type { HeaderBarProps } from '@bestseller-bit/frontend-community.components.header-bar';

import { HeaderBar } from '@bestseller-bit/frontend-community.components.header-bar';
import { OfflineAlert } from '@bestseller-bit/frontend-community.components.offline-alert';
import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { useIsLargeScreen } from '@bestseller-bit/frontend-community.theme.buying-theme';
import Box from '@mui/material/Box';

import { FilterButton } from './FilterButton';

interface CardHeaderProps extends Partial<HeaderBarProps> {
    filtersValues?: FilterButtonProps['filterValues'];
    filterGroups?: FilterButtonProps['filterGroups'];
    onClearAllFilters?: () => void;
    disableEscape?: boolean;
}

export const CardHeader = ({
    filtersValues,
    filterGroups,
    onClearAllFilters,
    disableEscape = false,
    ...headerBarProps
}: CardHeaderProps): ReactElement => {
    const isLargeScreen = useIsLargeScreen();

    useKeyboardShortcut({
        keyCombo: { code: 'Escape', ctrlKey: false, altKey: false },
        disabled: disableEscape,
        callback: () => {
            // it checks if there are any dialogs open
            const dialogs = Array.from(
                document.querySelectorAll(
                    '[role="dialog"][aria-modal="true"], .MuiDialog-root[open], .MuiDialog-paper'
                )
            );
            const isAnyDialogVisible = dialogs.some((dialog) => {
                const style = window.getComputedStyle(dialog as HTMLElement);
                return (
                    style.display !== 'none' &&
                    style.visibility !== 'hidden' &&
                    style.opacity !== '0'
                );
            });
            if (isAnyDialogVisible) {
                // If there are dialogs open, we do not close the card
                return;
            }

            // If no dialogs are open, we close the card
            headerBarProps.onClose?.();
        },
    });

    const shouldShowFilterButton =
        !!filtersValues && !!filterGroups && !!onClearAllFilters;

    /** If any of the filter props is provided, this useEffect throws an error if not all of them are provided */
    useEffect(() => {
        const shouldIncludeAllFilterProps =
            !!filtersValues || !!filterGroups || !!onClearAllFilters;
        if (
            shouldIncludeAllFilterProps !== !!filtersValues ||
            shouldIncludeAllFilterProps !== !!filterGroups ||
            shouldIncludeAllFilterProps !== !!onClearAllFilters
        ) {
            throw new Error(
                'CardHeader: Either provide all or none of these props: filtersValues, filterGroups, onClearAllFilters'
            );
        }
    }, [filterGroups, filtersValues, onClearAllFilters]);

    return (
        <>
            <HeaderBar
                title={headerBarProps.title}
                disableEscapeButton={disableEscape}
                fullWidth
                // spread here does NOT override the props we set before, it just adds the rest of the props that are not explicitly set
                {...headerBarProps}
                // Remove this when we get rid of the filter button
                // Its used to fake align the filter button as previous
                {...(shouldShowFilterButton && {
                    searchBarProps: {
                        ...headerBarProps.searchBarProps,
                        sx: {
                            marginRight: isLargeScreen ? '14px' : '34px',
                        },
                    },
                })}
                customizedComponents={
                    shouldShowFilterButton ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flex: '1 1 auto',
                            }}
                        >
                            <FilterButton
                                filterValues={filtersValues}
                                filterGroups={filterGroups}
                                sx={{
                                    marginLeft: isLargeScreen
                                        ? '-14px'
                                        : '-34px', // Magic numbers to align with design
                                }}
                                onClearAllFiltersClick={() => {
                                    onClearAllFilters();
                                }}
                            />
                        </Box>
                    ) : (
                        headerBarProps.customizedComponents
                    )
                }
            />
            <OfflineAlert />
        </>
    );
};
